import { gql, type TypedDocumentNode } from '@apollo/client'
import { QueryRef, useApolloClient, useMutation, useReadQuery } from '@apollo/client/react'
import { ConfirmDialog, ErrorAlert, PaginationControl, SearchExpand } from '@repo/shared-client/components/blocks'
import { NativeSelectFormField } from '@repo/shared-client/components/fields'
import {
  Badge,
  Button,
  Card,
  CardContent,
  Empty,
  EmptyDescription,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle,
  Spinner,
} from '@repo/shared-client/components/ui'
import { Form, useForm } from '@repo/shared-client/form'
import { cn } from '@repo/shared-client/lib/utils'
import { createFileRoute, stripSearchParams, useNavigate, useRouterState, useSearch } from '@tanstack/react-router'
import SFProfileBanner from '@web/src/components/primatives/sf-profile-banner/sf-profile-banner'
import EventAttendeesHeader from '@web/src/features/event/event-attendees-header/event-attendees-header'
import { queryAndPreload } from '@web/src/lib/apollo/query-and-preload'
import { env } from '@web/src/lib/env.client'
import { EventJoinRequestInfo, ProfileBasicInfo, ProfileMediaInfo } from '@web/src/lib/fragments'
import { useTranslation } from '@web/src/lib/intl/react'
import { robotsMetaForEnvironment, seo } from '@web/src/lib/seo'
import dayjs from 'dayjs'
import relativeTime from 'dayjs/plugin/relativeTime'
import { CheckIcon, UserSearchIcon, XIcon } from 'lucide-react'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import toast from 'react-hot-toast'
import type {
  ApproveEventJoinRequestMutation,
  ApproveEventJoinRequestMutationVariables,
  EventAttendeesPageQuery,
  EventAttendeesRequestPageQuery,
  EventAttendeesRequestPageQueryVariables,
  EventJoinRequestSortEnum,
  EventJoinRequestStatus,
} from 'types/graphql'
import { z } from 'zod'
import { EVENT_QUERY } from './$eventIdOrSlug_.attendees'

dayjs.extend(relativeTime)

export const QUERY: TypedDocumentNode<EventAttendeesRequestPageQuery, EventAttendeesRequestPageQueryVariables> = gql`
  query EventAttendeesRequestPageQuery(
    $page: Int
    $filter: EventJoinRequestsFilter!
    $sortBy: EventJoinRequestSortEnum
  ) {
    eventJoinRequestsPage(page: $page, filter: $filter, sortBy: $sortBy) {
      entries {
        ...EventJoinRequestInfo
      }
      count
      perPage
    }
  }
  ${EventJoinRequestInfo}
  ${ProfileBasicInfo}
  ${ProfileMediaInfo}
`

const EVENT_JOIN_REQUEST_APPROVE: TypedDocumentNode<
  ApproveEventJoinRequestMutation,
  ApproveEventJoinRequestMutationVariables
> = gql`
  mutation ApproveEventJoinRequestMutation($id: String!, $approve: Boolean!) {
    eventJoinRequestApprove(id: $id, approve: $approve) {
      ...EventJoinRequestInfo
    }
  }
  ${EventJoinRequestInfo}
  ${ProfileBasicInfo}
  ${ProfileMediaInfo}
`

type EventRequestsSearchInput = {
  page?: number
  name?: string
  status?: string
  sortBy?: string
}

type EventRequestsLoaderDeps = {
  page: number
  name?: string
  status: string
  sortBy: string
}

function resolveEventRequestsSearchParams(search: EventRequestsSearchInput): EventRequestsLoaderDeps {
  return {
    page: search.page ?? 1,
    name: search.name,
    status: search.status ?? 'PENDING',
    sortBy: search.sortBy ?? 'NEWEST',
  }
}

function eventJoinRequestsQueryVariables(
  eventIdOrSlug: string,
  deps: EventRequestsLoaderDeps
): EventAttendeesRequestPageQueryVariables {
  return {
    page: deps.page,
    filter: {
      eventId: eventIdOrSlug,
      name: deps.name,
      status: deps.status === 'ALL' ? undefined : (deps.status as EventJoinRequestStatus),
    },
    sortBy: deps.sortBy as EventJoinRequestSortEnum,
  }
}

export const Route = createFileRoute('/(auth)/(main-layout)/event/$eventIdOrSlug_/requests')({
  component: EventAttendeesRequestPage,
  validateSearch: z.object({
    page: z.coerce.number().optional(),
    name: z.string().optional(),
    status: z.string().optional(),
    sortBy: z.string().optional(),
  }),
  search: {
    middlewares: [
      // Removes parameters if they match default values
      stripSearchParams({
        page: 1,
        name: '',
        status: 'PENDING',
        sortBy: 'NEWEST',
      }),
    ],
  },
  head: () =>
    seo({
      title: `Attendees Request List | ${env.VITE_APP_NAME}`,
      description: 'Browse a list of event attendees requests.',
      robots: robotsMetaForEnvironment({ all: 'noindex,nofollow' }),
    }),
  loaderDeps: ({ search }) => resolveEventRequestsSearchParams(search),
  loader: async ({ context, params, deps }) => {
    const resolvedDeps = deps as EventRequestsLoaderDeps
    const variables = eventJoinRequestsQueryVariables(params.eventIdOrSlug, resolvedDeps)

    const [{ queryRef: eventQueryRef }, { queryRef: requestsQueryRef }] = await Promise.all([
      queryAndPreload(context, EVENT_QUERY, {
        variables: { eventIdOrSlug: params.eventIdOrSlug },
      }),
      queryAndPreload(context, QUERY, { variables }),
    ])

    return {
      eventQueryRef,
      requestsQueryRef,
    }
  },
})

function EventAttendeesRequestPage() {
  'use no memo' // Disable react compiler, page crashes without this

  const { t } = useTranslation()
  const search = useSearch({ strict: false })
  const resolvedSearch = useMemo(() => resolveEventRequestsSearchParams(search), [search])
  const navigate = useNavigate()
  const client = useApolloClient()
  const formRef = useRef(null)

  // State for reject confirmation modal
  const [rejectConfirmModal, setRejectConfirmModal] = useState(false)
  const [requestToReject, setRequestToReject] = useState<{ id: string; profileName: string } | null>(null)

  const { eventQueryRef, requestsQueryRef } = Route.useLoaderData()
  const { data: eventData } = useReadQuery(eventQueryRef as QueryRef<EventAttendeesPageQuery>)
  const { data, error } = useReadQuery(requestsQueryRef as QueryRef<EventAttendeesRequestPageQuery>)
  const [isLoading, setIsLoading] = useState(false)

  const routerIsPending = useRouterState({ select: (s) => s.status === 'pending' })

  // Prevent hydration mismatch by only setting loading state on client
  useEffect(() => {
    setIsLoading(routerIsPending)
  }, [routerIsPending])

  const formMethods = useForm({
    defaultValues: {
      page: resolvedSearch.page,
      name: resolvedSearch.name ?? '',
      status: resolvedSearch.status,
      sortBy: resolvedSearch.sortBy,
    },
  })

  const [eventJoinRequestApprove, { loading: isRespondLoading }] = useMutation(EVENT_JOIN_REQUEST_APPROVE, {
    onCompleted: (mutationData) => {
      const approved = mutationData.eventJoinRequestApprove.status === 'APPROVED'
      toast.success(approved ? t('Join request approved successfully') : t('Join request rejected successfully'))
      client.refetchQueries({ include: [QUERY] })
    },
    onError: (err) => {
      toast.error(err.message)
    },
  })

  const filterResults = useCallback(() => {
    const curPage = formMethods.getValues('page')
    const name = formMethods.getValues('name')
    const status = formMethods.getValues('status') as EventJoinRequestStatus
    const sortBy = formMethods.getValues('sortBy') as EventJoinRequestSortEnum

    navigate({
      search: {
        ...search,
        page: curPage,
        name: name || undefined,
        sortBy: sortBy,
        status: status || undefined,
      },
      replace: true,
      viewTransition: false,
    })
  }, [formMethods, navigate, search])

  const handleResetFilters = () => {
    navigate({
      search: {
        page: 1,
        name: undefined,
        sortBy: 'NEWEST',
        status: 'PENDING',
      },
      replace: true,
      viewTransition: false,
    } as never)
  }

  const handlePageChange = useCallback(
    (nextPage: { page: number }) => {
      navigate({
        search: {
          ...search,
          page: nextPage.page,
        },
        replace: true,
        viewTransition: false,
      })
    },
    [navigate, search]
  )

  const handleSearchChange = useCallback(
    (value: string) => {
      navigate({
        search: {
          ...search,
          page: 1,
          name: value || undefined,
        },
        replace: true,
        viewTransition: false,
      })
    },
    [navigate, search]
  )

  const handleResponse = (requestId: string, approve: boolean) => {
    eventJoinRequestApprove({
      variables: {
        id: requestId,
        approve,
      },
    })
  }

  const handleRejectClick = (requestId: string, profileName: string) => {
    setRequestToReject({ id: requestId, profileName })
    setRejectConfirmModal(true)
  }

  const confirmRejectRequest = () => {
    if (requestToReject) {
      handleResponse(requestToReject.id, false)
      setRejectConfirmModal(false)
      setRequestToReject(null)
    }
  }

  const pageData = useMemo(
    () => data?.eventJoinRequestsPage || { entries: [], count: 0, perPage: 24 },
    [data?.eventJoinRequestsPage]
  )
  const currentPage = resolvedSearch.page

  if (error) {
    return <ErrorAlert error={error} />
  }

  return (
    <div className="mx-auto w-full max-w-4xl p-2">
      <div className="flex w-full flex-col items-stretch gap-4">
        <EventAttendeesHeader activeTab={'requests'} event={eventData?.event} />

        <Form className="w-full" formMethods={formMethods} ref={formRef}>
          <div className="flex w-full flex-col items-stretch gap-4">
            {/* Search and Filters */}
            <SearchExpand
              clearSearchLabel={t('Clear search')}
              inputProps={{ 'aria-label': t('Search attendees') }}
              loading={isLoading}
              minChars={0}
              name="name"
              onSearchChange={handleSearchChange}
              placeholder={t('Search attendees by name...')}
              toggleFiltersLabel={t('Toggle filters')}
            >
              <Card className="gap-0 py-0">
                <CardContent className="px-3 py-3">
                  <div className="flex w-full flex-col gap-4">
                    <div className="flex w-full flex-wrap items-start gap-4">
                      <NativeSelectFormField label={t('Status')} name="status" onValueChange={filterResults}>
                        <option value="">
                          {t('All Requests ({{count}})', {
                            count: (eventData as EventAttendeesPageQuery)?.event?.joinRequestCounts?.totalCount || 0,
                          })}
                        </option>
                        <option value="PENDING">
                          {t('Pending ({{count}})', {
                            count: (eventData as EventAttendeesPageQuery)?.event?.joinRequestCounts?.pendingCount || 0,
                          })}
                        </option>
                        <option value="APPROVED">
                          {t('Approved ({{count}})', {
                            count: (eventData as EventAttendeesPageQuery)?.event?.joinRequestCounts?.approvedCount || 0,
                          })}
                        </option>
                        <option value="REJECTED">
                          {t('Rejected ({{count}})', {
                            count: (eventData as EventAttendeesPageQuery)?.event?.joinRequestCounts?.rejectedCount || 0,
                          })}
                        </option>
                      </NativeSelectFormField>

                      <NativeSelectFormField label={t('Sort By')} name="sortBy" onValueChange={filterResults}>
                        <option value="NEWEST">{t('Newest')}</option>
                        <option value="OLDEST">{t('Oldest')}</option>
                        <option value="NAME">{t('Name')}</option>
                        <option value="STATUS">{t('Status')}</option>
                      </NativeSelectFormField>
                    </div>

                    <div className="flex w-full justify-end">
                      <Button onClick={handleResetFilters} type="button" variant="secondary">
                        {t('Reset')}
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </SearchExpand>

            {/* Results */}
            {pageData.entries.length > 0 && (
              <div className="flex w-full flex-col gap-1">
                {pageData.entries.map((request) => (
                  <Card
                    className="w-full cursor-pointer overflow-hidden border-border bg-muted/50 py-0 transition-colors hover:border-ring hover:bg-muted data-[size=sm]:py-1"
                    key={request.id}
                    size="sm"
                  >
                    <CardContent className="px-3 py-0">
                      <div className="flex w-full items-center justify-between gap-2">
                        <SFProfileBanner
                          className="cursor-pointer"
                          lowerTitle={
                            <div className="flex flex-col items-start gap-1">
                              <Badge
                                className={cn(
                                  'gap-1 rounded-sm px-2 shadow-sm',
                                  request.status === 'PENDING' && 'bg-amber-500 text-black',
                                  request.status === 'APPROVED' && 'bg-green-600 text-primary-foreground',
                                  request.status === 'REJECTED' && 'bg-primary text-destructive-foreground'
                                )}
                                size="sm"
                                variant="default"
                              >
                                {t(request.status)}
                              </Badge>
                              <p className="text-muted-foreground text-xs">
                                {t('Requested: {{time}}', { time: dayjs(request.createdAt).fromNow() })}
                              </p>
                              {request.respondedAt && request.status !== 'PENDING' && (
                                <p className="text-muted-foreground text-xs">
                                  {t('Responded: {{time}}', { time: dayjs(request.respondedAt).fromNow() })}
                                  {request.respondedBy && t(' by {{name}}', { name: request.respondedBy.name })}
                                </p>
                              )}
                            </div>
                          }
                          onClick={() =>
                            navigate({
                              to: '/profile/$profileId/about',
                              params: { profileId: request.profile.id },
                            } as never)
                          }
                          profile={request.profile}
                          size="xl"
                        />

                        {request.status === 'PENDING' && (
                          <div className="flex shrink-0 items-center gap-2">
                            <Button
                              disabled={isRespondLoading}
                              onClick={() => handleRejectClick(request.id, request.profile.name)}
                              size="sm"
                              type="button"
                              variant="outline"
                            >
                              {isRespondLoading ? <Spinner className="size-4" /> : <XIcon className="size-4" />}
                              {t('Reject')}
                            </Button>
                            <Button
                              disabled={isRespondLoading}
                              onClick={() => handleResponse(request.id, true)}
                              size="sm"
                              type="button"
                            >
                              {isRespondLoading ? <Spinner className="size-4" /> : <CheckIcon className="size-4" />}
                              {t('Approve')}
                            </Button>
                          </div>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                ))}

                <PaginationControl
                  count={pageData.count}
                  onPageChange={handlePageChange}
                  page={currentPage}
                  pageSize={pageData.perPage}
                />
              </div>
            )}

            {pageData.entries.length === 0 && (
              <Empty className="py-20">
                <EmptyHeader>
                  <EmptyMedia variant="icon">
                    <UserSearchIcon />
                  </EmptyMedia>
                  <EmptyTitle>{t('No join requests found')}</EmptyTitle>
                  {resolvedSearch.name && (
                    <EmptyDescription>{t('Try adjusting your search or filters.')}</EmptyDescription>
                  )}
                </EmptyHeader>
              </Empty>
            )}
          </div>
        </Form>
      </div>

      {/* Reject Confirmation Modal */}
      <ConfirmDialog
        cancelText={t('Cancel')}
        confirmText={t('Reject')}
        loading={isRespondLoading}
        onConfirm={confirmRejectRequest}
        onOpenChange={(e) => {
          setRejectConfirmModal(e.open)
          if (!e.open) {
            setRequestToReject(null)
          }
        }}
        open={rejectConfirmModal}
        text={t('Are you sure you want to reject the join request from {{name}}?', {
          name: requestToReject?.profileName,
        })}
        title={t('Reject Join Request')}
        variant="delete"
      />
    </div>
  )
}
